import { useEffect } from "react";

const BRAND = "Catalyst BH Consulting";
const OG_IMG = "https://catalystbhconsulting.com/og-catalyst.svg";

/**
 * Lightweight SEO helper. Sets title + meta tags + JSON-LD without extra deps.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = OG_IMG,
  jsonLd,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${BRAND}` : `${BRAND}`;
    document.title = fullTitle;

    const canonical = `https://catalystbhconsulting.com${path}`;

    const tags = [
      { name: "description", content: description },
      { property: "og:title", content: fullTitle },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: image },
      { property: "og:url", content: canonical },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: fullTitle },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
    ];

    const created = [];

    tags.forEach(({ name, property, content }) => {
      if (!content) return;
      const key = name ? "name" : "property";
      const value = name || property;
      let el = document.head.querySelector(`meta[${key}="${value}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, value);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    });

    // Canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
      created.push(link);
    }
    link.setAttribute("href", canonical);

    // JSON-LD
    let script = document.head.querySelector('script[data-seo="jsonld"]');
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-seo", "jsonld");
        document.head.appendChild(script);
        created.push(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      created.forEach((el) => el.remove());
    };
  }, [title, description, path, image, jsonLd]);

  return null;
}

// -------------------- Preset schemas --------------------

export const founders = [
  {
    "@type": "Person",
    name: "James McCreary",
    honorificSuffix: "LPC-S",
    jobTitle: "Co-Founder & CEO",
    worksFor: { "@type": "Organization", name: "Catalyst BH Consulting" },
    sameAs: ["https://www.linkedin.com/in/james-mccreary-ms-lpc"],
  },
  {
    "@type": "Person",
    name: "Jessica McCreary",
    honorificSuffix: "SHRM-CP",
    jobTitle: "Co-Founder & Principal HR Consultant",
    worksFor: { "@type": "Organization", name: "Catalyst BH Consulting" },
    sameAs: ["https://www.linkedin.com/in/jessica-tappero/"],
  },
  {
    "@type": "Person",
    name: "Kristin Hale",
    honorificSuffix: "LPC-S",
    jobTitle: "Co-Founder & Principal Compliance Consultant",
    worksFor: { "@type": "Organization", name: "Catalyst BH Consulting" },
    sameAs: ["https://www.linkedin.com/in/kristin-hale/"],
  },
];

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": ["ProfessionalService", "LocalBusiness"],
  "@id": "https://catalystbhconsulting.com/#org",
  name: "Catalyst BH Consulting",
  alternateName: "Catalyst Behavioral Health Consulting, LLC",
  url: "https://catalystbhconsulting.com",
  logo: "https://catalystbhconsulting.com/logo.png",
  image: [
    "https://catalystbhconsulting.com/logo.png",
    "https://catalystbhconsulting.com/og-catalyst.svg",
  ],
  description:
    "Behavioral health consulting, HR consulting, and program development helping organizations build, grow, and thrive.",
  slogan: "One partner. Every stage of growth.",
  priceRange: "$$",
  telephone: "+1-623-324-3237",
  email: "info@catalystbhconsulting.com",
  currenciesAccepted: "USD",
  paymentAccepted: ["Credit Card", "ACH", "Invoice"],
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "State", name: "Arizona" },
    { "@type": "City", name: "Phoenix" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Phoenix",
    addressRegion: "AZ",
    postalCode: "85001",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 33.4484,
    longitude: -112.0740,
  },
  hasMap: "https://www.google.com/maps/place/Phoenix,+AZ",
  contactPoint: [{
    "@type": "ContactPoint",
    telephone: "+1-623-324-3237",
    email: "info@catalystbhconsulting.com",
    contactType: "customer service",
    areaServed: "US",
    availableLanguage: ["English"],
  }],
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:00",
  }],
  founder: founders,
  knowsAbout: [
    "Behavioral Health Consulting",
    "Joint Commission Accreditation",
    "CARF Accreditation",
    "Behavioral Health HR",
    "Intensive Outpatient Program Development",
    "Residential Treatment Program Development",
    "Behavioral Health Compliance",
    "Fractional HR Leadership",
  ],
  sameAs: [
    "https://www.linkedin.com/company/catalyst-behavioral-health-consulting",
    "https://facebook.com/catalystbhconsulting.com",
  ],
  makesOffer: [
    { "@type": "Offer", name: "Behavioral Health Consulting", url: "https://catalystbhconsulting.com/solutions" },
    { "@type": "Offer", name: "HR Consulting", url: "https://catalystbhconsulting.com/hr" },
    { "@type": "Offer", name: "Catalyst Continuum — Program Development", url: "https://catalystbhconsulting.com/continuum" },
    { "@type": "Offer", name: "Catalyst Academy — Digital Toolkits & Courses", url: "https://catalystbhconsulting.com/academy" },
  ],
};

// About-page schema — combines AboutPage + the Organization + its founders
// in a single @graph so Google can associate founder Person entities with
// the Organization and surface them in Knowledge Panels.
export const aboutPageJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": "https://catalystbhconsulting.com/about#aboutpage",
      url: "https://catalystbhconsulting.com/about",
      name: "About · Catalyst BH Consulting",
      description:
        "Meet the clinicians and operators behind Catalyst BH Consulting — behavioral health, HR, and compliance advisors serving programs nationwide.",
      about: { "@id": "https://catalystbhconsulting.com/#org" },
    },
    {
      ...orgJsonLd,
      "@id": "https://catalystbhconsulting.com/#org",
    },
  ],
};

export const hrOrgJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Catalyst HR Consulting",
  url: "https://hr.catalystbhconsulting.com",
  description:
    "Fractional HR leadership, compliance audits, investigations, and culture consulting for healthcare and mission-driven organizations.",
  parentOrganization: { "@type": "Organization", name: "Catalyst BH Consulting" },
};

export const continuumOrgJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Catalyst Continuum",
  url: "https://continuum.catalystbhconsulting.com",
  description:
    "Behavioral health program development across the full continuum of care — from startup to multi-site operations.",
  parentOrganization: { "@type": "Organization", name: "Catalyst BH Consulting" },
};


// -------------------- Reusable schema helpers --------------------

const BASE_URL = "https://catalystbhconsulting.com";

/**
 * breadcrumbJsonLd([['Home','/'],['Solutions','/solutions'],['Behavioral Health']])
 * — last item is treated as the current page (no url required).
 */
export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    ...(path ? { item: `${BASE_URL}${path}` } : {}),
  })),
});

/**
 * articleJsonLd({ title, description, slug, image, publishedAt, updatedAt, author })
 * — for blog posts. Produces the BlogPosting entity Google prefers for
 * article rich results (headline, datePublished, author, image).
 */
export const articleJsonLd = ({
  title,
  description,
  slug,
  image,
  publishedAt,
  updatedAt,
  author = "Catalyst BH Consulting",
}) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: title,
  description,
  mainEntityOfPage: { "@type": "WebPage", "@id": `${BASE_URL}/blog/${slug}` },
  image: image ? [image] : ["https://catalystbhconsulting.com/og-catalyst.svg"],
  datePublished: publishedAt,
  dateModified: updatedAt || publishedAt,
  author: { "@type": "Person", name: author },
  publisher: {
    "@type": "Organization",
    name: "Catalyst BH Consulting",
    logo: {
      "@type": "ImageObject",
      url: "https://catalystbhconsulting.com/logo.png",
    },
  },
});

/**
 * faqJsonLd([{ q, a }, ...]) — FAQPage schema. Boosts rich-result CTR
 * because Google can show expandable Q&A directly in search results.
 */
export const faqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

/** serviceJsonLd({ name, description, url, category }) — used per division. */
export const serviceJsonLd = ({ name, description, url, category, areaServed = "US" }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  serviceType: category,
  url,
  areaServed: { "@type": "Country", name: areaServed },
  provider: { "@type": "Organization", name: "Catalyst BH Consulting", url: BASE_URL },
});
